import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import Pagination from '@/client/components/common/Pagination';
import { useListFilters } from '@/client/hooks/useListFilters';
import { useVirtualTourEventsQuery } from '@/client/api/listing-management/virtual-tours.query';
import { getApiErrorMessage } from '@/client/utils/errors';
import { fmtDate, StatusBadge } from './shared';

const HOURS_OPTIONS = [
  { value: '24', label: 'Last 24h' },
  { value: '168', label: 'Last 7d' },
  { value: '720', label: 'Last 30d' },
];

export const ErrorsTab: React.FC = () => {
  const { filters, page, limit, handleFilterChange, handlePageChange, handleLimitChange } = useListFilters({
    initialFilters: { outcome: '', op: '', hours: '168' },
    namespace: 'err',
  });

  const [, setSearchParams] = useSearchParams();
  const openTour = (vtid: number) => setSearchParams({ tab: 'tours', tour: String(vtid) });

  const { data, isLoading, isError, error } = useVirtualTourEventsQuery({
    outcome: filters.outcome || undefined,
    op: filters.op || undefined,
    hours: Number(filters.hours) || 168,
    page,
    page_size: limit,
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-white p-3">
        <select
          className="rounded-md border border-input bg-background px-3 py-2 text-sm"
          value={filters.outcome}
          onChange={(e) => handleFilterChange('outcome', e.target.value)}
        >
          <option value="">All outcomes</option>
          <option value="ok">Ok</option>
          <option value="error">Error</option>
          <option value="blocked">Blocked</option>
        </select>
        <Input
          className="w-40"
          placeholder="Operation (op)"
          value={filters.op}
          onChange={(e) => handleFilterChange('op', e.target.value)}
        />
        <select
          className="rounded-md border border-input bg-background px-3 py-2 text-sm"
          value={filters.hours}
          onChange={(e) => handleFilterChange('hours', e.target.value)}
        >
          {HOURS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      {data?.by_code?.length ? (
        <div className="flex flex-wrap gap-2 rounded-lg border border-gray-200 bg-white p-3">
          <span className="text-xs font-medium text-gray-400">By error code:</span>
          {data.by_code.map((c) => (
            <span key={c.code} className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs text-gray-700">
              {c.code} <span className="text-gray-400">×{c.n}</span>
            </span>
          ))}
        </div>
      ) : null}

      {isError ? (
        <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-6 text-sm text-red-600">
          {getApiErrorMessage(error, 'Could not load events.')}
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">When</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Op</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Outcome</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Provider</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Listing</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Message</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {isLoading && !data
                ? Array.from({ length: limit }).map((_, i) => (
                    <tr key={i}>
                      <td className="px-4 py-3" colSpan={6}>
                        <Skeleton className="h-5 w-full" />
                      </td>
                    </tr>
                  ))
                : (data?.events ?? []).map((ev) => (
                    <tr key={ev.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-500">{fmtDate(ev.created_at)}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{ev.op}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <StatusBadge status={ev.outcome} />
                          {ev.error_code && <span className="text-xs text-gray-400">{ev.error_code}</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{ev.provider || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                          {ev.eid ? (
                            <Link to={`/admin/listings/${ev.eid}`} className="text-blue-600 hover:underline">
                              #{ev.eid}
                            </Link>
                          ) : (
                            '—'
                          )}
                          {ev.vtid != null && (
                            <button onClick={() => openTour(ev.vtid as number)} className="text-xs text-blue-600 hover:underline">
                              tour {ev.vtid}
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">{ev.message || '—'}</td>
                    </tr>
                  ))}
              {!isLoading && (data?.events?.length ?? 0) === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-sm text-gray-500">
                    No events in this window.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
        <Pagination
          total={data?.total ?? 0}
          page={page}
          itemsPerPage={limit}
          limit={limit}
          setPage={handlePageChange}
          setItemsPerPage={handleLimitChange}
          showButtons
          showSelect
          selectOptions={[10, 20, 30, 50]}
        />
      </div>
    </div>
  );
};

export default ErrorsTab;
